(() => {
  const root = document.querySelector('[data-laptop-keuzehulp]');
  if (!root) return;

  const state = {
    step: 0,
    answers: { budgets: [], grades: [], screens: [], storages: [], memory: [], usages: [], extras: [] },
    products: [],
    resultProducts: [],
    bestMatchId: null,
    premiumPickId: null,
    isFallbackResults: false,
    easyFlowCache: {},
    usedEmergencyFallback: false,
    usedFallbackBase: false
  };

  let productsLoadPromise;

  const usageRequirements = {
    basic: { cpu: 0, ram: 0, gpuRequired: false, gpuPreferred: false, minStorage: 0 },
    school: { cpu: 3, ram: 8, gpuRequired: false, gpuPreferred: false, minStorage: 256 },
    business: { cpu: 3, ram: 8, gpuRequired: false, gpuPreferred: false, minStorage: 256 },
    creative: { cpu: 5, ram: 16, gpuRequired: false, gpuPreferred: true, minStorage: 256 },
    'light-gaming': { cpu: 5, ram: 16, gpuRequired: false, gpuPreferred: true, minStorage: 256 },
    'heavy-gaming': { cpu: 7, ram: 16, gpuRequired: true, gpuPreferred: true, minStorage: 512 }
  };

  const usageValueTargets = {
    basic: { idealCpu: 3, idealRam: 8, idealStorage: 256, comfortPrice: 450 },
    school: { idealCpu: 5, idealRam: 8, idealStorage: 256, comfortPrice: 650 },
    business: { idealCpu: 5, idealRam: 16, idealStorage: 512, comfortPrice: 750 },
    creative: { idealCpu: 7, idealRam: 16, idealStorage: 512, comfortPrice: 1000 },
    'light-gaming': { idealCpu: 7, idealRam: 16, idealStorage: 512, comfortPrice: 1000 },
    'heavy-gaming': { idealCpu: 9, idealRam: 32, idealStorage: 1000, comfortPrice: 1400 }
  };

  const elements = {
    intro: root.querySelector('[data-step="intro"]'),
    wizard: root.querySelector('[data-step="wizard"]'),
    results: root.querySelector('[data-step="results"]'),
    form: root.querySelector('[data-wizard-form]'),
    questions: [...root.querySelectorAll('[data-question]')],
    btnStart: root.querySelector('[data-action="start"]'),
    btnNext: root.querySelector('[data-action="next"]'),
    btnPrev: root.querySelector('[data-action="prev"]'),
    btnRestart: root.querySelector('[data-action="restart"]'),
    btnResultsRestartTop: root.querySelector('[data-action="results-restart-top"]'),
    btnResultsBack: root.querySelector('[data-action="results-back"]'),
    btnResultsBackBottom: root.querySelector('[data-action="results-back-bottom"]'),
    sortSelect: root.querySelector('[data-results-sort]'),
    progressFill: root.querySelector('[data-progress-fill]'),
    progressText: root.querySelector('[data-progress-text]'),
    progressPercent: root.querySelector('[data-progress-percent]'),
    resultsSummary: root.querySelector('[data-results-summary]'),
    fallbackNote: root.querySelector('[data-fallback-note]'),
    resultsList: root.querySelector('[data-results-list]'),
    stepError: root.querySelector('[data-step-error]')
  };

  const budgetRanges = {
    under350: [0, 350],
    under500: [0, 500],
    under750: [0, 750],
    any: [0, Number.POSITIVE_INFINITY]
  };

  const extraOptions = {
    touchscreen: { label: 'Touchscreen', surcharge: 75 },
    hdmi: { label: 'HDMI', surcharge: 15 },
    'usb-c': { label: 'USB-C', surcharge: 25 }
  };

  const getSelectedBudgets = () => state.answers.budgets?.length ? state.answers.budgets : [];
  const getSelectedGrades = () => state.answers.grades?.length ? state.answers.grades : ['any'];
  const getSelectedUsages = () => state.answers.usages?.length ? state.answers.usages : ['basic'];
  const getSelectedStorages = () => state.answers.storages?.length ? state.answers.storages : ['any'];
  const getSelectedMemory = () => state.answers.memory?.length ? state.answers.memory : ['any'];
  const hasBudgetPreference = () => {
    const budgets = getSelectedBudgets();
    return budgets.length > 0 && !budgets.includes('any');
  };
  const getBudgetBounds = () => {
    const budgets = getSelectedBudgets();
    if (!budgets.length || budgets.includes('any')) return budgetRanges.any;
    return budgets.reduce((bounds, budget) => {
      const range = budgetRanges[budget] || budgetRanges.any;
      return [Math.min(bounds[0], range[0]), Math.max(bounds[1], range[1])];
    }, [Number.POSITIVE_INFINITY, 0]);
  };
  const matchesBudget = (product) => {
    const budgets = getSelectedBudgets();
    if (!budgets.length || budgets.includes('any')) return true;
    return budgets.some((budget) => {
      const [minBudget, maxBudget] = budgetRanges[budget] || budgetRanges.any;
      return product.price >= minBudget && product.price <= maxBudget;
    });
  };
  const getRequestedStorage = () => {
    const storages = getSelectedStorages().filter((storage) => storage !== 'any').map(Number).filter(Boolean);
    return storages.length ? Math.min(...storages) : 0;
  };
  const getRequestedMemory = () => {
    const memory = getSelectedMemory().filter((value) => value !== 'any').map(Number).filter(Boolean);
    return memory.length ? Math.min(...memory) : 0;
  };

  const gradeLabels = {
    'a-grade': 'A-Grade',
    'b-grade': 'B-Grade',
    'c-grade': 'C-Grade',
    doosschade: 'Doosschade'
  };

  const normalizeGrade = (value) => {
    const grade = toText(value).replace(/\s+/g, '-');
    return grade === 'spot' ? 'c-grade' : grade;
  };
  const hasGradePreference = () => {
    const grades = getSelectedGrades();
    return grades.length > 0 && !grades.includes('any');
  };
  const buildVariantUrl = (url, variantId) => {
    if (!variantId) return url;
    const separator = String(url || '').includes('?') ? '&' : '?';
    return `${url}${separator}variant=${variantId}`;
  };

  const toText = (value) => String(value || '').trim().toLowerCase();
  const parseNumber = (value) => Number((String(value || '').replace(',', '.').match(/\d+(\.\d+)?/) || [0])[0]);
  const parseStorageGb = (value) => {
    const text = toText(value);
    if (!text) return 0;
    const amount = parseNumber(text);
    return text.includes('tb') ? Math.round(amount * 1000) : Math.round(amount);
  };

  const parseCpuTier = (processor, processorType) => {
    const txt = `${toText(processor)} ${toText(processorType)}`;
    if (txt.includes('apple m4') || txt.includes('apple m3') || txt.includes('apple m2 pro') || txt.includes('apple m2 max') || txt.includes('apple m1 pro') || txt.includes('apple m1 max')) return 9;
    if (txt.includes('apple m2') || txt.includes('apple m1')) return 7;
    if (txt.includes('i9') || txt.includes('ryzen 9')) return 9;
    if (txt.includes('i7') || txt.includes('ryzen 7')) return 7;
    if (txt.includes('i5') || txt.includes('ryzen 5')) return 5;
    if (txt.includes('i3') || txt.includes('ryzen 3')) return 3;
    return 1;
  };

  const parseBoolean = (value) => ['ja', 'yes', 'true', '1', 'aanwezig'].some((flag) => toText(value).includes(flag));

  const hasGpu = (videochip) => ['nvidia', 'geforce', 'rtx', 'radeon'].some((flag) => toText(videochip).includes(flag));

  const normalizeVariant = (variant) => {
    const optionValues = [variant.option1, variant.option2, variant.option3, variant.title].filter(Boolean);
    const grade = optionValues.map(normalizeGrade).find((value) => gradeLabels[value]) || '';
    return {
      id: variant.id,
      title: variant.title || '',
      available: Boolean(variant.available),
      price: Number(variant.price || 0),
      compareAtPrice: Number(variant.compare_at_price || 0),
      grade
    };
  };

  const normalizeProduct = (product) => {
    const metafields = product.metafields || {};
    const upgradeData = product.upgrade || product.storage_upgrade || {};
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: product.url || `/products/${product.handle}`,
      image: product.image || product.featured_image,
      price: Number(product.price || 0),
      compareAtPrice: Number(product.compare_at_price || 0),
      variants: Array.isArray(product.variants) ? product.variants.map(normalizeVariant) : [],
      specs: {
        screen: parseNumber(metafields.schermgrootte),
        cpuTier: parseCpuTier(metafields.processor, metafields.processor_type),
        cpuText: metafields.processor || metafields.processor_type || 'Processor onbekend',
        ram: parseNumber(metafields.intern_werkgeheugen),
        storage: parseStorageGb(metafields.harde_schijf),
        weight: parseNumber(metafields.gewicht),
        touchscreen: parseBoolean(metafields.touchscreen),
        webcam: parseBoolean(metafields.webcam),
        hdmi: toText(metafields.videoaansluiting).includes('hdmi'),
        usbc: ['usb-c', 'usb c', 'type-c', 'type c', 'thunderbolt'].some((flag) => toText(metafields.usb).includes(flag)),
        gpu: hasGpu(metafields.videochip)
      },
      upgrade: {
        storageUpgradeable: parseBoolean(upgradeData.storage_upgradeable ?? upgradeData.upgradeable),
        storagePrice512: parseNumber(upgradeData.price_512 ?? upgradeData.storage_price_512),
        storagePrice1000: parseNumber(upgradeData.price_1000 ?? upgradeData.storage_price_1000),
        storagePrice2000: parseNumber(upgradeData.price_2000 ?? upgradeData.storage_price_2000)
      }
    };
  };

  const saveAnswers = () => {
    const formData = new FormData(elements.form);
    state.answers.budgets = formData.getAll('budget');
    state.answers.grades = formData.getAll('grade');
    state.answers.usages = formData.getAll('usage');
    state.answers.screens = formData.getAll('screen');
    state.answers.storages = formData.getAll('storage');
    state.answers.memory = formData.getAll('memory');
    state.answers.extras = formData.getAll('extras');
  };

  const applyGradeSelection = (product, { allowAlternative = false } = {}) => {
    const availableVariants = (product.variants || []).filter((variant) => variant.available && variant.price > 0);
    if (!availableVariants.length) return product;

    const selectedGrades = getSelectedGrades();
    const selectedGradeKeys = selectedGrades.filter((grade) => grade !== 'any').map(normalizeGrade);
    const hasPreference = selectedGradeKeys.length > 0;
    const matchingVariants = hasPreference
      ? availableVariants.filter((variant) => selectedGradeKeys.includes(variant.grade))
      : availableVariants;
    const chosenVariant = (matchingVariants.length ? matchingVariants : (allowAlternative ? availableVariants : []))
      .sort((a, b) => a.price - b.price)[0];

    if (!chosenVariant) return null;

    const isGradeAlternative = hasPreference && !matchingVariants.length;
    const selectedGradeLabels = selectedGradeKeys.map((grade) => gradeLabels[grade] || grade).join(', ');
    const chosenGradeLabel = gradeLabels[chosenVariant.grade] || chosenVariant.title || 'andere staat';

    return {
      ...product,
      price: chosenVariant.price,
      compareAtPrice: chosenVariant.compareAtPrice || product.compareAtPrice,
      url: buildVariantUrl(product.url, chosenVariant.id),
      selectedVariantId: chosenVariant.id,
      selectedGrade: chosenVariant.grade,
      selectedGradeLabel: chosenGradeLabel,
      isGradeAlternative,
      gradeGaps: isGradeAlternative
        ? [{ key: 'grade', label: `${selectedGradeLabels} niet beschikbaar; getoond als ${chosenGradeLabel}` }]
        : []
    };
  };

  const getCandidateProducts = ({ allowGradeAlternative = false } = {}) => state.products
    .map((product) => applyGradeSelection(product, { allowAlternative: allowGradeAlternative }))
    .filter(Boolean);

  const matchesScreen = (selectedScreens, screen) => {
    if (!selectedScreens?.length || selectedScreens.includes('any')) return true;
    return selectedScreens.some((selected) => {
      if (selected === '12-13') return screen >= 12 && screen <= 13.9;
      if (selected === '14') return screen >= 14 && screen < 15;
      if (selected === '15-16') return screen >= 15 && screen <= 16.9;
      return false;
    });
  };

  const getExtraAvailability = (product, extras) => {
    const map = { touchscreen: product.specs.touchscreen, hdmi: product.specs.hdmi, 'usb-c': product.specs.usbc };
    return extras.map((extra) => {
      const config = extraOptions[extra];
      return {
        key: extra,
        label: config?.label || extra,
        available: Boolean(map[extra]),
        surcharge: Number(config?.surcharge || 0)
      };
    });
  };

  const meetsExtraRequirements = (product) => {
    if (state.answers.extras.includes('touchscreen') && !product.specs.touchscreen) return false;
    if (state.answers.extras.includes('usb-c') && !product.specs.usbc) return false;
    return true;
  };

  const getCombinedUsageProfile = () => getSelectedUsages().reduce((profile, usage) => {
    const req = usageRequirements[usage] || usageRequirements.basic;
    const target = usageValueTargets[usage] || usageValueTargets.basic;
    return {
      req: {
        cpu: Math.max(profile.req.cpu, req.cpu),
        ram: Math.max(profile.req.ram, req.ram),
        gpuRequired: profile.req.gpuRequired || req.gpuRequired,
        gpuPreferred: profile.req.gpuPreferred || req.gpuPreferred,
        minStorage: Math.max(profile.req.minStorage, req.minStorage)
      },
      target: {
        idealCpu: Math.max(profile.target.idealCpu, target.idealCpu),
        idealRam: Math.max(profile.target.idealRam, target.idealRam),
        idealStorage: Math.max(profile.target.idealStorage, target.idealStorage),
        comfortPrice: Math.max(profile.target.comfortPrice, target.comfortPrice)
      }
    };
  }, { req: { ...usageRequirements.basic }, target: { ...usageValueTargets.basic } });

  const getStorageUpgrade = (product, requestedStorage) => {
    const currentStorage = Number(product.specs.storage || 0);
    const targetStorage = Number(requestedStorage || 0);
    if (!targetStorage || targetStorage <= currentStorage) return { needed: false, possible: false, surcharge: 0, targetStorage, currentStorage };

    if (!product.upgrade?.storageUpgradeable) {
      return { needed: true, possible: false, surcharge: 0, targetStorage, currentStorage };
    }

    const priceMap = {
      512: Number(product.upgrade.storagePrice512 || 0),
      1000: Number(product.upgrade.storagePrice1000 || 0),
      2000: Number(product.upgrade.storagePrice2000 || 0)
    };

    const closestTier = Object.keys(priceMap)
      .map(Number)
      .filter((tier) => tier >= targetStorage)
      .sort((a, b) => a - b)[0] || targetStorage;
    const surcharge = Number(priceMap[closestTier] || 0);
    return { needed: true, possible: surcharge > 0, surcharge, targetStorage: closestTier, currentStorage };
  };

  const meetsUsageRequirements = (product) => {
    const { req } = getCombinedUsageProfile();
    if (product.specs.cpuTier < req.cpu) return false;
    if (product.specs.ram < req.ram) return false;
      if (req.gpuRequired && !product.specs.gpu) return false;
      if (product.specs.storage < req.minStorage) return false;
      if (getRequestedMemory() && product.specs.ram < getRequestedMemory()) return false;
    return true;
  };

  const getUsageGaps = (product) => {
    const { req } = getCombinedUsageProfile();
    const gaps = [];
    if (product.specs.cpuTier < req.cpu || (req.gpuRequired && !product.specs.gpu)) gaps.push({ key: 'performance', label: 'Minder geschikt voor zware games' });
    if (product.specs.ram < req.ram) gaps.push({ key: 'ram', label: `Voor dit gebruik adviseren we ${req.ram}GB werkgeheugen` });
    if (getRequestedStorage() && product.specs.storage < getRequestedStorage()) gaps.push({ key: 'storage', label: `Opslag is lager dan je gekozen ${getRequestedStorage()}GB` });
    if (getRequestedMemory() && product.specs.ram < getRequestedMemory()) gaps.push({ key: 'ram', label: `Je koos ${getRequestedMemory()}GB werkgeheugen` });
    return gaps;
  };

  const filterProducts = () => {
    return getCandidateProducts().filter((product) => {
      if (!matchesBudget(product)) return false;
      if (!matchesScreen(state.answers.screens, product.specs.screen)) return false;
      if (!meetsUsageRequirements(product)) return false;
      if (!meetsExtraRequirements(product)) return false;
      return true;
    });
  };

  const buildFallbackProducts = () => {
    const [minBudget, maxBudget] = getBudgetBounds();
    const requestedStorage = getRequestedStorage();
    const requestedMemory = getRequestedMemory();

    let candidates = getCandidateProducts({ allowGradeAlternative: true }).filter((product) =>
      product.price >= minBudget &&
      product.price <= maxBudget &&
      matchesScreen(state.answers.screens, product.specs.screen) &&
      meetsExtraRequirements(product)
    );

    if (!candidates.length) {
      candidates = getCandidateProducts({ allowGradeAlternative: true }).filter((product) => matchesScreen(state.answers.screens, product.specs.screen) && meetsExtraRequirements(product));
    }
    if (!candidates.length) candidates = getCandidateProducts({ allowGradeAlternative: true });

    return candidates
      .map((product) => {
        const { req } = getCombinedUsageProfile();
        let score = 40;
        score -= Math.max(0, req.cpu - product.specs.cpuTier) * 3;
        score -= Math.max(0, req.ram - product.specs.ram) / 2;
        if (req.gpuRequired && !product.specs.gpu) score -= 6;
        if (requestedStorage > 0 && product.specs.storage < requestedStorage) score -= 2;
        if (product.specs.storage >= requestedStorage && requestedStorage > 0) score += 2;
        if (requestedMemory > 0 && product.specs.ram < requestedMemory) score -= 4;
        if (requestedMemory > 0 && product.specs.ram >= requestedMemory) score += 2;
        score += Math.max(0, product.specs.cpuTier - req.cpu);

        const storagePriority = requestedStorage
          ? (product.specs.storage === requestedStorage ? 0 : product.specs.storage > requestedStorage ? 1 : 2)
          : 0;

        const storageUpgrade = getStorageUpgrade(product, requestedStorage);
        const usageGaps = getUsageGaps(product);
        const reasons = usageGaps.length
          ? ['Valt binnen je voorkeuren, maar is niet volledig geschikt']
          : ['Alternatief dat het dichtst bij jouw voorkeuren ligt'];
        if (storageUpgrade.needed && storageUpgrade.possible) {
          reasons.push(`Opslag upgradebaar naar ${storageUpgrade.targetStorage >= 1000 ? `${Math.round(storageUpgrade.targetStorage / 1000)}TB` : `${storageUpgrade.targetStorage}GB`} (+ ${formatPriceNl(storageUpgrade.surcharge)})`);
        }
        return { ...product, score, storagePriority, storageUpgrade, extraSurcharge: storageUpgrade.surcharge, usageGaps, isUsageFallback: usageGaps.length > 0, reasons };
      })
      .sort((a, b) => a.storagePriority - b.storagePriority || b.score - a.score || a.price - b.price);
  };

  const scoreProducts = (products) => {
    const requestedStorage = getRequestedStorage();
    const requestedMemory = getRequestedMemory();
    const hasActiveBudgetPreference = hasBudgetPreference();
    const [, maxBudget] = getBudgetBounds();

    return products.map((product) => {
      const { req, target: valueTarget } = getCombinedUsageProfile();
      let score = 0;
      const reasons = [];
      const extras = getExtraAvailability(product, state.answers.extras);
      const missingExtras = extras.filter((extra) => !extra.available);
      const availableExtras = extras.filter((extra) => extra.available);
      const extraSurcharge = missingExtras.reduce((sum, extra) => sum + extra.surcharge, 0);
      const storageUpgrade = getStorageUpgrade(product, requestedStorage);
      const totalSurcharge = extraSurcharge + (storageUpgrade.needed ? storageUpgrade.surcharge : 0);
      const effectiveStorageTarget = requestedStorage || valueTarget.idealStorage;
      const priceTarget = hasActiveBudgetPreference && Number.isFinite(maxBudget) ? maxBudget : valueTarget.comfortPrice;
      const overTargetPrice = Math.max(0, product.price - priceTarget);

      score += Math.min(Math.max(0, product.specs.cpuTier - req.cpu), Math.max(0, valueTarget.idealCpu - req.cpu)) * 1.4;
      score += Math.min(Math.max(0, product.specs.ram - req.ram), Math.max(0, valueTarget.idealRam - req.ram)) / 4;
      score += product.specs.cpuTier >= req.cpu ? 4 : -4;
      score += product.specs.ram >= req.ram ? 3 : -3;
      score -= overTargetPrice / 250;

      if (requestedStorage > 0 && product.specs.storage >= requestedStorage) {
        score += 3;
        reasons.push('Opslag sluit goed aan op je voorkeur');
      }
      if (requestedStorage > 0 && product.specs.storage < requestedStorage) {
        score -= 1.5;
      }
      if (!requestedStorage && product.specs.storage >= effectiveStorageTarget) {
        score += 1.5;
      }

      if (req.gpuPreferred && product.specs.gpu) {
        score += 2;
        reasons.push('Videokaart matcht met jouw gebruik');
      }

      if (req.cpu >= 7 && product.specs.cpuTier >= req.cpu) reasons.push('Sterke processor voor zwaarder gebruik');
      if (product.specs.ram >= req.ram) reasons.push('Genoeg werkgeheugen voor multitasken');
      if (requestedMemory > 0 && product.specs.ram >= requestedMemory) reasons.push(`${product.specs.ram}GB werkgeheugen`);
      if (hasActiveBudgetPreference && product.price <= maxBudget) reasons.push('Past binnen je budget');
      if (!hasActiveBudgetPreference && product.price <= valueTarget.comfortPrice) reasons.push('Goede prijs voor dit gebruik');
      if (availableExtras.length) reasons.push(`Beschikt al over: ${availableExtras.map((extra) => extra.label).join(', ')}`);
      if (missingExtras.length) reasons.push(`Mogelijk met meerprijs: ${formatPriceNl(extraSurcharge)}`);
      if (storageUpgrade.needed && storageUpgrade.possible) reasons.push(`Opslag upgradebaar naar ${storageUpgrade.targetStorage >= 1000 ? `${Math.round(storageUpgrade.targetStorage / 1000)}TB` : `${storageUpgrade.targetStorage}GB`}`);
      if (storageUpgrade.needed && !storageUpgrade.possible) reasons.push('Opslagupgrade niet beschikbaar voor dit model');

      const storagePriority = requestedStorage
        ? (product.specs.storage === requestedStorage ? 0 : product.specs.storage > requestedStorage ? 1 : 2)
        : 0;
      const memoryPriority = requestedMemory
        ? (product.specs.ram === requestedMemory ? 0 : product.specs.ram > requestedMemory ? 1 : 2)
        : 0;

      return { ...product, score, storagePriority, memoryPriority, storageUpgrade, extraSurcharge: totalSurcharge, reasons: [...new Set(reasons)].slice(0, 3) };
    }).sort((a, b) => a.storagePriority - b.storagePriority || a.memoryPriority - b.memoryPriority || b.score - a.score || a.price - b.price);
  };

  const formatPriceNl = (value) => `${Math.round(Number(value || 0)).toLocaleString('nl-NL')},-`;
  const formatPriceNlCents = (valueCents) => formatPriceNl(Number(valueCents || 0) / 100);

  const parseStorageTargetGb = (title) => {
    const text = toText(title);
    if (!text || text.includes('geen upgrade')) return 0;
    const amount = parseNumber(text);
    const parsed = text.includes('tb') ? Math.round(amount * 1000) : Math.round(amount);
    if (parsed >= 940 && parsed <= 980) return 1000;
    if (parsed >= 470 && parsed <= 490) return 512;
    return parsed;
  };

  const parseRamTargetGb = (title) => parseNumber(title);
  const formatStorageUpgradeTitle = (title, storageGb) => {
    const cleanTitle = String(title || '').match(/\b(\d+(?:[,.]\d+)?)\s*(TB|GB)\b/i)?.[0];
    if (cleanTitle) return cleanTitle.replace(/\s+/g, '').toUpperCase();
    return storageGb >= 1000 ? `${Math.round(storageGb / 1000)}TB` : `${storageGb}GB`;
  };
  const formatRamUpgradeTitle = (title, ramGb) => {
    const cleanTitle = String(title || '').match(/\b(8|16|24|32|64|96|128)\s*GB\b/i)?.[0];
    return cleanTitle ? cleanTitle.replace(/\s+/g, '') : `${ramGb}GB`;
  };

  const getEasyFlowInputTitle = (input) => [
    input.getAttribute('data-value-title'),
    input.getAttribute('data-title'),
    input.getAttribute('aria-label'),
    input.getAttribute('value'),
    input.closest('label')?.textContent,
    input.parentElement?.textContent
  ].filter(Boolean).join(' ');

  const fetchEasyFlowOption = async (product, optionMatchers, parser, cacheSuffix, optionFilter = () => true) => {
    if (!product?.url) return null;
    const cacheKey = `${product.url}::${cacheSuffix}`;
    if (state.easyFlowCache[cacheKey] !== undefined) return state.easyFlowCache[cacheKey];

    try {
      const response = await fetch(product.url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to fetch product page');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const optionRoots = [...doc.querySelectorAll('.ef__product-option')];
      let optionRoot = optionRoots.find((rootEl) => {
        const title = toText(rootEl.getAttribute('data-option-title'));
        return optionMatchers.some((matcher) => title.includes(matcher));
      });
      if (!optionRoot && cacheSuffix === 'ram') {
        optionRoot = optionRoots.find((rootEl) => [...rootEl.querySelectorAll('input.ef__product-option-input')].some((input) => {
          const title = input.getAttribute('data-value-title') || '';
          const targetGb = parser(title);
          return optionFilter({ title, targetGb });
        }));
      }
      if (!optionRoot) {
        state.easyFlowCache[cacheKey] = null;
        return null;
      }

      const options = [...optionRoot.querySelectorAll('input.ef__product-option-input')]
        .map((input) => {
          const title = getEasyFlowInputTitle(input);
          return {
            title,
            priceCents: Number(input.getAttribute('data-value-price') || 0),
            variantId: input.getAttribute('data-variant-id') || '',
            targetGb: parser(title)
          };
        })
        .filter((option) => option.targetGb > 0 && optionFilter(option));

      state.easyFlowCache[cacheKey] = options;
      return options;
    } catch (error) {
      state.easyFlowCache[cacheKey] = null;
      return null;
    }
  };

  const fetchEasyFlowSsdUpgrade = async (product) => {
    const options = await fetchEasyFlowOption(product, ['ssd', 'opslag', 'storage'], parseStorageTargetGb, 'ssd');
    return options?.map((option) => ({ ...option, storageGb: option.targetGb })) || null;
  };

  const fetchEasyFlowRamUpgrade = async (product) => {
    const options = await fetchEasyFlowOption(
      product,
      ['ram', 'werkgeheugen', 'intern werkgeheugen', 'geheugen', 'memory'],
      parseRamTargetGb,
      'ram',
      (option) => {
        const title = toText(option.title);
        return option.targetGb >= 4 && option.targetGb <= 128 && !title.includes('ssd') && !title.includes('tb');
      }
    );
    return options?.map((option) => ({ ...option, ramGb: option.targetGb })) || null;
  };

  const findValidSsdUpgrade = (product, requestedStorage) => async (candidate) => {
    const upgrades = await fetchEasyFlowSsdUpgrade(candidate);
    if (!upgrades?.length) return null;
    const currentStorage = Number(candidate.specs.storage || 0);
    const valid = upgrades
      .filter((upgrade) => upgrade.storageGb >= requestedStorage && upgrade.storageGb > currentStorage)
      .sort((a, b) => a.storageGb - b.storageGb || a.priceCents - b.priceCents)[0];
    if (!valid) return null;
    return {
      ...candidate,
      easyFlowUpgrade: {
        title: formatStorageUpgradeTitle(valid.title, valid.storageGb),
        storageGb: valid.storageGb,
        priceCents: valid.priceCents,
        variantId: valid.variantId,
        totalPrice: Number(candidate.price || 0) + (valid.priceCents / 100)
      }
    };
  };

  const findValidRamUpgrade = (requestedMemory) => async (candidate) => {
    if (!requestedMemory) return null;
    const upgrades = await fetchEasyFlowRamUpgrade(candidate);
    if (!upgrades?.length) return null;
    const currentRam = Number(candidate.specs.ram || 0);
    const valid = upgrades
      .filter((upgrade) => upgrade.ramGb >= requestedMemory && upgrade.ramGb > currentRam)
      .sort((a, b) => a.ramGb - b.ramGb || a.priceCents - b.priceCents)[0];
    if (!valid) return null;
    return {
      ...candidate,
      easyFlowRamUpgrade: {
        title: formatRamUpgradeTitle(valid.title, valid.ramGb),
        ramGb: valid.ramGb,
        priceCents: valid.priceCents,
        variantId: valid.variantId,
        totalPrice: Number(candidate.price || 0) + (valid.priceCents / 100)
      }
    };
  };

  const enrichRamUpgrades = async (products, requestedMemory) => {
    if (!requestedMemory || !products.length) return products;
    const resolver = findValidRamUpgrade(requestedMemory);
    return Promise.all(products.map((product) => {
      const needsRamUpgrade = product.usageGaps?.some((gap) => gap.key === 'ram') || product.specs.ram < requestedMemory;
      if (!needsRamUpgrade) return Promise.resolve(product);
      return resolver(product).then((upgraded) => upgraded || product).catch(() => product);
    }));
  };

  const renderProductCards = (products, { showUpgradeBadge = false } = {}) => products.map((product) => {
    const badge = showUpgradeBadge ? 'Upgradebaar alternatief' : (product.id === state.bestMatchId && !product.isUsageFallback ? 'Beste keuze' : product.id === state.premiumPickId ? 'Premium pick' : '');
    const badgeMarkup = badge ? `<span class="lkh__card-badge">${badge}</span>` : '';
    const reasons = (product.reasons.length ? product.reasons : ['Goede allround keuze'])
      .slice(0, 2)
      .map((reason) => `<li><span class="lkh__usp-check" aria-hidden="true">✓</span>${reason}</li>`)
      .join('');
    const solvedGapKeys = [
      product.easyFlowUpgrade ? 'storage' : null,
      product.easyFlowRamUpgrade ? 'ram' : null
    ].filter(Boolean);
    const allGaps = [...(product.gradeGaps || []), ...(product.usageGaps || [])];
    const gapReasons = allGaps.length
      ? allGaps
        .filter((gap, index, gaps) => gaps.findIndex((item) => item.key === gap.key && item.label === gap.label) === index)
        .filter((gap) => !solvedGapKeys.includes(gap.key))
        .map((gap) => `<li class="lkh__gap-item"><span class="lkh__usp-miss" aria-hidden="true">×</span>${gap.label}</li>`)
        .join('')
      : '';
    const oldPrice = product.compareAtPrice > product.price ? `<span class="lkh__old-price">${formatPriceNl(product.compareAtPrice)}</span>` : '';
    const surchargeLabel = product.extraSurcharge > 0 ? `<small class="lkh__extra-price">v.a. + ${formatPriceNl(product.extraSurcharge)} voor upgrades/extra's</small>` : '';
    const upgradeDetails = product.easyFlowUpgrade
      ? `<li class="lkh__upgrade-line"><span class="lkh__usp-check" aria-hidden="true">✓</span>Gewenste SSD mogelijk: ${product.easyFlowUpgrade.title} voor +${formatPriceNlCents(product.easyFlowUpgrade.priceCents)}</li>`
      : '';
    const ramUpgradeDetails = product.easyFlowRamUpgrade
      ? `<li class="lkh__upgrade-line"><span class="lkh__usp-check" aria-hidden="true">✓</span>Gewenste RAM mogelijk: ${product.easyFlowRamUpgrade.title} voor +${formatPriceNlCents(product.easyFlowRamUpgrade.priceCents)}</li>`
      : '';

    return `
      <article class="lkh__card ${product.id === state.bestMatchId && !showUpgradeBadge && !product.isUsageFallback ? 'is-best' : ''}">
        <a class="lkh__card-link" href="${product.url}" target="_blank" rel="noopener noreferrer">
          <div class="lkh__media">
            ${badgeMarkup}
            <img src="${product.image || ''}" alt="${product.title}" loading="lazy">
          </div>
          <h3>${product.title}</h3>
          <ul class="lkh__spec-list">
            <li>${product.specs.screen || '?'}" FHD</li>
            <li>${product.specs.cpuText}</li>
            <li>${product.specs.ram || '?'}GB DDR4</li>
            <li>${product.specs.storage || '?'}GB SSD</li>
          </ul>
          <ul class="lkh__usp-list">${gapReasons || reasons}${upgradeDetails}${ramUpgradeDetails}</ul>
          <p class="lkh__price"><strong>v.a. ${formatPriceNl(product.price)}</strong>${oldPrice}${surchargeLabel}</p>
          <span class="lkh__btn lkh__btn--primary lkh__btn--full">Bekijk laptop</span>
        </a>
      </article>
    `;
  }).join('');

  const renderResults = ({ perfectMatches, upgradeAlternatives, cheaperAlternatives = [], supplementalAlternatives = [] }) => {
    const matches = [...perfectMatches, ...upgradeAlternatives, ...cheaperAlternatives, ...supplementalAlternatives];
    elements.fallbackNote?.classList.toggle('is-hidden', !state.isFallbackResults);
    if (state.isFallbackResults && elements.fallbackNote) {
      elements.fallbackNote.textContent = 'Geen perfecte match gevonden. Hieronder tonen we vergelijkbare resultaten en/of upgradebare alternatieven.';
    }
    if (state.usedFallbackBase || state.usedEmergencyFallback) {
      elements.resultsSummary.textContent = `We hebben geen perfecte match gevonden, maar wel ${matches.length} vergelijkbare alternatieven.`;
    } else if (cheaperAlternatives.length || supplementalAlternatives.length) {
      const extrasCount = cheaperAlternatives.length + supplementalAlternatives.length;
      elements.resultsSummary.textContent = `Op basis van jouw voorkeuren hebben we ${perfectMatches.length} goede ${perfectMatches.length === 1 ? 'match' : 'matches'} gevonden en ${extrasCount} extra ${extrasCount === 1 ? 'alternatief' : 'alternatieven'} geselecteerd.`;
    } else if (!perfectMatches.length && upgradeAlternatives.length) {
      elements.resultsSummary.textContent = 'We hebben geen perfecte match gevonden, maar wel goede alternatieven. Deze laptops kunnen met een SSD-upgrade beter aansluiten.';
    } else {
      elements.resultsSummary.textContent = `Op basis van jouw voorkeuren hebben we ${matches.length} laptops geselecteerd.`;
    }

    const perfectMarkup = perfectMatches.length
      ? `<div class="lkh__cards-group"><h3 class="lkh__group-title">${(state.usedEmergencyFallback || state.usedFallbackBase) ? 'Vergelijkbare alternatieven' : 'Perfecte matches'}</h3><div class="lkh__cards-grid">${renderProductCards(perfectMatches)}</div></div>`
      : '';
    const upgradeMarkup = upgradeAlternatives.length
      ? `<div class="lkh__cards-group"><h3 class="lkh__group-title">Upgradebare alternatieven</h3><p class="lkh__group-note">Deze laptops voldoen niet standaard aan je opslagvoorkeur, maar kunnen via een SSD-upgrade passend worden gemaakt.</p><div class="lkh__cards-grid">${renderProductCards(upgradeAlternatives, { showUpgradeBadge: true })}</div></div>`
      : '';
    const cheaperMarkup = cheaperAlternatives.length
      ? `<div class="lkh__cards-group"><h3 class="lkh__group-title">Goedkopere alternatieven</h3><p class="lkh__group-note">Deze laptops vallen onder je gekozen budget en zijn nog steeds geschikt voor jouw gebruik.</p><div class="lkh__cards-grid">${renderProductCards(cheaperAlternatives)}</div></div>`
      : '';
    const supplementalMarkup = supplementalAlternatives.length
      ? `<div class="lkh__cards-group"><h3 class="lkh__group-title">Aanvullende alternatieven</h3><p class="lkh__group-note">Deze laptops tonen we erbij voor extra keuze. Ze zijn passend voor jouw gebruik, maar vallen minder goed binnen je budgetvoorkeur.</p><div class="lkh__cards-grid">${renderProductCards(supplementalAlternatives)}</div></div>`
      : '';
    elements.resultsList.innerHTML = perfectMarkup + upgradeMarkup + cheaperMarkup + supplementalMarkup;
  };

  const findPremiumPickId = (products, bestMatchId) => {
    const bestMatch = products.find((product) => product.id === bestMatchId);
    if (!bestMatch) return null;

    const premiumPick = products
      .filter((product) => product.id !== bestMatchId && product.price > bestMatch.price)
      .sort((a, b) => b.price - a.price || b.score - a.score)[0];

    return premiumPick?.id || null;
  };

  const applyResultSorting = (mode) => {
    const products = [...state.resultProducts];
    if (mode === 'price-asc') {
      products.sort((a, b) => a.price - b.price || b.score - a.score);
    } else if (mode === 'price-desc') {
      products.sort((a, b) => b.price - a.price || b.score - a.score);
    } else {
      products.sort((a, b) => a.storagePriority - b.storagePriority || a.memoryPriority - b.memoryPriority || b.score - a.score || a.price - b.price);
    }
    renderResults({
      perfectMatches: products.filter((p) => !p.easyFlowUpgrade && !p.isCheaperAlternative && !p.isSupplementalAlternative),
      upgradeAlternatives: products.filter((p) => p.easyFlowUpgrade),
      cheaperAlternatives: products.filter((p) => p.isCheaperAlternative),
      supplementalAlternatives: products.filter((p) => p.isSupplementalAlternative)
    });
  };

  const validateCurrentStep = () => {
    const question = elements.questions[state.step];
    if (question.dataset.question === 'display-extras') {
      return question.querySelectorAll('input[name="screen"]:checked').length > 0;
    }
    return [...new Set([...question.querySelectorAll('input[required]')].map((input) => input.name))]
      .every((name) => question.querySelector(`input[name="${name}"]:checked`));
  };

  const showStep = () => {
    elements.questions.forEach((question, index) => question.classList.toggle('is-hidden', index !== state.step));
    elements.stepError?.classList.add('is-hidden');
    const displayStep = state.step + 1;
    const totalSteps = elements.questions.length + 1;
    const pct = Math.round((displayStep / totalSteps) * 100);
    elements.progressFill.style.width = `${pct}%`;
    elements.progressText.textContent = `Stap ${displayStep} van ${totalSteps}`;
    elements.progressPercent.textContent = `${pct}%`;
    elements.btnPrev.disabled = state.step === 0;
    elements.btnNext.textContent = state.step === elements.questions.length - 1 ? 'Bekijk resultaten →' : 'Volgende →';
  };

  const showResults = async () => {
    saveAnswers();
    const originalButtonText = elements.btnNext.textContent;
    elements.btnNext.disabled = true;
    elements.btnNext.textContent = 'Resultaten laden...';
    try {
      const requestedStorage = getRequestedStorage();
      const requestedMemory = getRequestedMemory();
      const requiredMemory = Math.max(requestedMemory, getCombinedUsageProfile().req.ram);
      const [minBudget, maxBudget] = getBudgetBounds();
      const hasActiveBudgetPreference = hasBudgetPreference();
      if (productsLoadPromise) await productsLoadPromise;
      const sorted = scoreProducts(filterProducts());
      state.usedFallbackBase = !sorted.length;
      const fallback = !sorted.length ? buildFallbackProducts() : [];
      const baseResults = sorted.length ? sorted : fallback;
      const perfectMatches = baseResults.filter((p) => requestedStorage === 0 || p.specs.storage >= requestedStorage);
      const underStorageCandidates = requestedStorage > 256 ? baseResults.filter((p) => p.specs.storage < requestedStorage) : [];
      const resolver = findValidSsdUpgrade(null, requestedStorage);
      const resolved = await Promise.all(underStorageCandidates.slice(0, 8).map((product) => resolver(product).catch(() => null)));
      const upgradeAlternatives = resolved.filter(Boolean);
      let finalPerfect = perfectMatches;
      let finalUpgrade = upgradeAlternatives;
      let finalCheaper = [];
      let finalSupplemental = [];
      state.usedEmergencyFallback = false;
      if (!finalPerfect.length && !finalUpgrade.length) {
        const emergencyFallback = buildFallbackProducts().slice(0, 3);
        finalPerfect = emergencyFallback;
        state.usedEmergencyFallback = emergencyFallback.length > 0;
        state.isFallbackResults = emergencyFallback.length > 0;
        elements.resultsSummary.textContent = emergencyFallback.length
          ? `${emergencyFallback.length} vergelijkbare resultaten gevonden op basis van jouw antwoorden.`
          : 'Geen resultaten gevonden. Probeer minder strenge voorkeuren.';
      }
      finalPerfect = await enrichRamUpgrades(finalPerfect, requiredMemory);
      finalUpgrade = await enrichRamUpgrades(finalUpgrade, requiredMemory);

      state.resultProducts = [...finalPerfect, ...finalUpgrade];
      if (hasActiveBudgetPreference && minBudget > 0) {
        finalCheaper = scoreProducts(
          getCandidateProducts({ allowGradeAlternative: true }).filter((product) =>
            product.price < minBudget &&
            matchesScreen(state.answers.screens, product.specs.screen) &&
            meetsUsageRequirements(product) &&
            meetsExtraRequirements(product) &&
            (requestedStorage === 0 || product.specs.storage >= requestedStorage) &&
            (requestedMemory === 0 || product.specs.ram >= requestedMemory) &&
            !state.resultProducts.some((picked) => picked.id === product.id)
          )
        )
          .slice(0, 4)
          .map((product) => ({ ...product, isCheaperAlternative: true }));
        finalCheaper = await enrichRamUpgrades(finalCheaper, requiredMemory);
        state.resultProducts = [...finalPerfect, ...finalUpgrade, ...finalCheaper];
      }
      if (state.resultProducts.length < 6) {
        const supplemental = scoreProducts(
          getCandidateProducts({ allowGradeAlternative: true }).filter((product) =>
            (!hasActiveBudgetPreference || product.price > maxBudget) &&
            matchesScreen(state.answers.screens, product.specs.screen) &&
            meetsUsageRequirements(product) &&
            meetsExtraRequirements(product) &&
            !state.resultProducts.some((picked) => picked.id === product.id)
          )
        ).slice(0, 6 - state.resultProducts.length);
        if (supplemental.length) {
          finalSupplemental = supplemental.map((product) => ({ ...product, isSupplementalAlternative: true }));
          finalSupplemental = await enrichRamUpgrades(finalSupplemental, requiredMemory);
          state.resultProducts = [...finalPerfect, ...finalUpgrade, ...finalCheaper, ...finalSupplemental];
        }
      }
      state.isFallbackResults = (state.usedFallbackBase || state.usedEmergencyFallback) && state.resultProducts.length > 0;
      state.bestMatchId = finalPerfect.find((product) => !product.isUsageFallback)?.id || null;
      state.premiumPickId = findPremiumPickId(finalPerfect, state.bestMatchId);
      elements.wizard.classList.add('is-hidden');
      elements.results.classList.remove('is-hidden');
      renderResults({ perfectMatches: finalPerfect, upgradeAlternatives: finalUpgrade, cheaperAlternatives: finalCheaper, supplementalAlternatives: finalSupplemental });
      elements.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: Math.max(0, root.getBoundingClientRect().top + window.scrollY - 20), behavior: 'smooth' });
    } catch (error) {
      console.error('Laptop keuzehulp resultaten fout:', error);
      elements.wizard.classList.add('is-hidden');
      elements.results.classList.remove('is-hidden');
      elements.resultsSummary.textContent = 'Er ging iets mis bij het laden van de resultaten. Probeer je voorkeuren iets ruimer te zetten.';
      elements.resultsList.innerHTML = '';
    } finally {
      elements.btnNext.disabled = false;
      elements.btnNext.textContent = originalButtonText;
    }
  };

  const loadProducts = () => {
    const config = window.laptopKeuzehulpConfig || {};
    const embedded = Array.isArray(config.products) ? config.products : [];
    if (embedded.length) {
      state.products = embedded.map(normalizeProduct);
      return Promise.resolve();
    }
    const url = config.productsJsonUrl || '/collections/refurbished-laptops/products.json?limit=250';
    return fetch(url)
      .then((res) => res.json())
      .then((data) => {
        state.products = (data.products || []).map((product) => normalizeProduct({ ...product, price: Number(product.variants?.[0]?.price || product.price || 0), metafields: {} }));
      })
      .catch(() => {
        state.products = [];
      });
  };

  elements.btnStart.addEventListener('click', () => {
    elements.intro.classList.add('is-hidden');
    elements.wizard.classList.remove('is-hidden');
    showStep();
  });

  elements.btnNext.addEventListener('click', () => {
    saveAnswers();
    if (!validateCurrentStep()) {
      if (elements.stepError) {
        elements.stepError.classList.remove('is-hidden');
        elements.stepError.textContent = 'Kies minimaal één optie om verder te gaan.';
      }
      return;
    }
    if (state.step === elements.questions.length - 1) return showResults();
    state.step += 1;
    showStep();
  });

  elements.btnPrev.addEventListener('click', () => {
    if (state.step === 0) return;
    state.step -= 1;
    showStep();
  });

  elements.btnRestart.addEventListener('click', () => {
    state.step = 0;
    elements.form.reset();
    elements.results.classList.add('is-hidden');
    elements.intro.classList.remove('is-hidden');
  });

  elements.btnResultsRestartTop?.addEventListener('click', () => {
    state.step = 0;
    elements.form.reset();
    elements.results.classList.add('is-hidden');
    elements.intro.classList.remove('is-hidden');
  });

  const goBackFromResults = () => {
    elements.results.classList.add('is-hidden');
    elements.wizard.classList.remove('is-hidden');
    state.step = elements.questions.length - 1;
    showStep();
  };

  const setupExclusiveAnyOptions = () => {
    ['grade', 'screen', 'storage', 'memory'].forEach((name) => {
      elements.form.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
        input.addEventListener('change', () => {
          const group = [...elements.form.querySelectorAll(`input[name="${name}"]`)];
          if (input.value === 'any' && input.checked) {
            group.filter((option) => option !== input).forEach((option) => {
              option.checked = false;
            });
            return;
          }
          if (input.checked) {
            const anyOption = group.find((option) => option.value === 'any');
            if (anyOption) anyOption.checked = false;
          }
        });
      });
    });
  };

  elements.btnResultsBack?.addEventListener('click', goBackFromResults);
  elements.btnResultsBackBottom?.addEventListener('click', goBackFromResults);
  elements.sortSelect?.addEventListener('change', (event) => {
    applyResultSorting(event.target.value);
  });

  setupExclusiveAnyOptions();
  productsLoadPromise = loadProducts();
})();
